import React from 'react'
import clsx from 'clsx'
import { Box as Body, Box, Box as Container, Box as Page } from '@node-space/ui-components'
import { AppBanners, AppBannerType } from 'components/app/AppBanners'
import { FlagEnabledBanner } from 'components/banners/FlagEnabledBanner'
import { LoginFooter } from 'components/footer/LoginFooter'
import { LogoHeader } from 'components/header/LogoHeader'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

export enum LoginPageLayoutVariant {
  DEFAULT = 'default',
  NARROW = 'narrow',
  WIDE = 'wide',
}

interface LoginPageLayoutProps {
  children: React.ReactNode
  title?: string
  description?: React.ReactNode
  headerRightContent?: React.ReactNode
  hasFooter?: boolean
  narrowView?: boolean
  variant?: LoginPageLayoutVariant
  alignTop?: boolean
}

const LoginPageLayout = ({
  children,
  title,
  description,
  headerRightContent,
  hasFooter,
  variant = LoginPageLayoutVariant.DEFAULT,
  alignTop = false,
}: LoginPageLayoutProps) => {
  const { enableBanner } = useFeatureFlags()

  const appBanners: AppBannerType[] = []

  if (enableBanner?.enabled) {
    appBanners.push(<FlagEnabledBanner {...enableBanner} />)
  }

  return (
    <>
      <AppBanners banners={appBanners} />

      <Page flex flexCol width="full" minHeight="screen" className="bg-background-flat ">
        <LogoHeader>{headerRightContent}</LogoHeader>
        <Container
          testId="login-page-body"
          flex
          flexSize="fill"
          width="full"
          centerChildren={!alignTop}
          paddingX={16}
          justify="center"
          className={clsx({ 'items-start': alignTop })}
        >
          <Body
            testId="login-page-body"
            flex
            flexCol
            width="full"
            paddingY={32}
            gapY={24}
            centerChildren
            className={clsx({
              'sm:px-8 max-w-[500px]': variant === LoginPageLayoutVariant.WIDE,
              'w-[384px]': variant === LoginPageLayoutVariant.NARROW,
            })}
          >
            <Box flex flexCol gapY={8} centerChildren>
              {title && (
                <h2
                  className="text-h2 text-foreground-prominent text-center"
                  data-testid="login-header-text"
                >
                  {title}
                </h2>
              )}
              {description && (
                <div className="text-body-strong text-foreground-subdued text-center">
                  {description}
                </div>
              )}
            </Box>
            {children}
          </Body>
        </Container>
        {hasFooter && <LoginFooter />}
      </Page>
    </>
  )
}

export default LoginPageLayout
